/**
 * ScanOpenHousesModal — configures a Realtor.ca open-house scan.
 *
 * Two modes:
 *   • City search — type a Canadian city + pick a search-area preset; the
 *     backend geocodes it (Nominatim) into a map boundary.
 *   • Paste Realtor.ca link — copy the address-bar URL of a manual Map
 *     search on realtor.ca; we reuse its exact boundary + open-house dates.
 *
 * Both feed the same pay-per-result API engine (see
 * `openhouse_api_engine.py`). Bad city / bad URL → HTTP 400 → toast, modal
 * stays open so the user can fix it.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Loader2, MapPin, Sparkles, Info, Link2, Building2, MapPinned } from "lucide-react";
import CreditEstimate from "@/components/billing/CreditEstimate";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import AreaPresetPicker, { AREA_PRESETS } from "@/components/openhouses/AreaPresetPicker";
import ScanBudgetControls from "@/components/openhouses/ScanBudgetControls";
import SearchUrlPreview from "@/components/openhouses/SearchUrlPreview";

const LS_KEYS = {
  mode: "openhouseScan.mode",
  city: "openhouseScan.city",
  searchUrl: "openhouseScan.searchUrl",
  expansion: "openhouseScan.expansion",
  maxResults: "openhouseScan.maxResults",
  usdCap: "openhouseScan.usdCap2",
  territoryId: "openhouseScan.territoryId",
};

const lsGet = (k, fallback) => {
  try { return localStorage.getItem(k) ?? fallback; } catch { return fallback; }
};
const lsSet = (k, v) => { try { localStorage.setItem(k, String(v)); } catch { /* ignore */ } };

const MODES = [
  { key: "city", label: "Search by city", Icon: Building2 },
  { key: "url", label: "Paste Realtor.ca link", Icon: Link2 },
  { key: "territory", label: "Territory", Icon: MapPinned },
];
const MODE_KEYS = MODES.map((m) => m.key);

function boxKm(box) {
  if (!box) return null;
  const midLat = ((box.north + box.south) / 2) * (Math.PI / 180);
  const width = Math.round(Math.abs(box.east - box.west) * 111.32 * Math.cos(midLat));
  const height = Math.round(Math.abs(box.north - box.south) * 110.57);
  return `${width} × ${height} km`;
}

export default function ScanOpenHousesModal({ open, onOpenChange, onScanStarted, initialMode, initialTerritoryId }) {
  const [mode, setMode] = useState(() => {
    const saved = lsGet(LS_KEYS.mode, "city");
    return MODE_KEYS.includes(saved) ? saved : "city";
  });
  const [city, setCity] = useState(() => lsGet(LS_KEYS.city, ""));
  const [searchUrl, setSearchUrl] = useState(() => lsGet(LS_KEYS.searchUrl, ""));
  const [urlPreview, setUrlPreview] = useState(null);
  const [expansion, setExpansion] = useState(() => lsGet(LS_KEYS.expansion, "city_plus_suburbs"));
  const [maxResults, setMaxResults] = useState(() => Number(lsGet(LS_KEYS.maxResults, 100)) || 100);
  const [usdCap, setUsdCap] = useState(() => Number(lsGet(LS_KEYS.usdCap, 1.0)) || 1.0);
  const [locations, setLocations] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [territoryId, setTerritoryId] = useState(() => lsGet(LS_KEYS.territoryId, ""));
  const [territoryDetail, setTerritoryDetail] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Deep-link (e.g. from the Territory page): pre-select mode + territory.
  useEffect(() => {
    if (!open) return;
    if (initialMode && MODE_KEYS.includes(initialMode)) setMode(initialMode);
    if (initialTerritoryId) setTerritoryId(initialTerritoryId);
  }, [open, initialMode, initialTerritoryId]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const [lRes, tRes] = await Promise.all([api.get("/locations"), api.get("/territories")]);
        if (cancelled) return;
        const lData = lRes.data;
        setLocations(Array.isArray(lData) ? lData : (lData?.locations || []));
        setTerritories(tRes.data?.territories || []);
      } catch { /* non-fatal — user can still type any city */ }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Load the chosen territory's box so we can show its size + location.
  useEffect(() => {
    if (!open || mode !== "territory" || !territoryId) { setTerritoryDetail(null); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/territories/${territoryId}`);
        if (!cancelled) setTerritoryDetail(data);
      } catch { if (!cancelled) setTerritoryDetail(null); }
    })();
    return () => { cancelled = true; };
  }, [open, mode, territoryId]);

  const cityOptions = useMemo(() => {
    const names = new Set();
    for (const loc of locations || []) {
      const n = (loc.name || "").trim();
      if (n) names.add(n);
    }
    return Array.from(names).sort();
  }, [locations]);

  const territoryLocation = useMemo(
    () => (locations || []).find((l) => l.id === territoryDetail?.location_id) || null,
    [locations, territoryDetail],
  );

  const [creditsOk, setCreditsOk] = useState(true);
  const disabled = submitting || !creditsOk
    || (mode === "city" ? !city.trim() : mode === "url" ? !urlPreview : !territoryId);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (disabled) return;
    setSubmitting(true);
    try {
      const payload = mode === "url"
        ? { search_url: searchUrl.trim() }
        : mode === "territory"
          ? { territory_id: territoryId }
          : { city: city.trim(), expansion };
      const { data } = await api.post("/openhouses/scan", {
        ...payload, max_results: Number(maxResults), usd_cap: Number(usdCap), oh_days: 4,
      });
      lsSet(LS_KEYS.mode, mode);
      lsSet(LS_KEYS.city, city.trim());
      lsSet(LS_KEYS.searchUrl, searchUrl.trim());
      lsSet(LS_KEYS.expansion, expansion);
      lsSet(LS_KEYS.maxResults, maxResults);
      lsSet(LS_KEYS.usdCap, usdCap);
      lsSet(LS_KEYS.territoryId, territoryId);
      const areaLabel = mode === "url"
        ? "your Realtor.ca link"
        : mode === "territory"
          ? "territory boundary"
          : AREA_PRESETS.find((a) => a.key === expansion)?.label;
      toast.success("Open-house scan started", {
        description: `${data.resolved_display_name || city} · ${areaLabel} · cap $${Number(data.usd_cap).toFixed(2)}`,
      });
      onOpenChange?.(false);
      onScanStarted?.(data);
    } catch (err) {
      toast.error("Scan failed to start", { description: formatApiError(err) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="scan-openhouses-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-rose-600" />
            Scan open houses
          </DialogTitle>
          <DialogDescription>
            Pulls every open house Realtor.ca lists for the next 4 days — with times, agent and
            brokerage — straight from its listing API. No browser, no timeouts.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-3 gap-1 rounded-md border border-slate-200 bg-slate-50 p-1" role="tablist">
            {MODES.map(({ key, label, Icon }) => {
              const active = mode === key;
              return (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setMode(key)}
                  data-testid={`scan-mode-${key}`}
                  className={
                    "inline-flex items-center justify-center gap-1.5 rounded px-2 py-2 text-xs sm:text-sm min-h-[40px] transition-colors " +
                    (active ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:text-slate-900")
                  }
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="truncate">{label}</span>
                </button>
              );
            })}
          </div>

          {mode === "territory" ? (
            <div data-testid="scan-territory-panel">
              <Label className="text-xs uppercase tracking-wide text-slate-600">Territory</Label>
              <Select value={territoryId} onValueChange={setTerritoryId} disabled={territories.length === 0}>
                <SelectTrigger className="mt-1 min-h-[44px]" data-testid="scan-territory-select">
                  <SelectValue placeholder={territories.length ? "Select a territory" : "No territories available"} />
                </SelectTrigger>
                <SelectContent>
                  {territories.map((t) => (
                    <SelectItem key={t.id} value={t.id} data-testid={`scan-territory-option-${t.id}`}>
                      {t.name} · {t.town_count} town{t.town_count === 1 ? "" : "s"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {territories.length === 0 ? (
                <p className="mt-1 text-[11px] text-slate-500">
                  Draw or pick a territory on the Territory Import page first.
                </p>
              ) : null}
              {territoryDetail ? (
                <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600 space-y-0.5" data-testid="scan-territory-summary">
                  <div>
                    Search box <span className="font-mono text-slate-800">{boxKm(territoryDetail.box) || "—"}</span>
                    {territoryDetail.boundary?.type === "polygon" ? " · listings outside your drawn shape are dropped" : ""}
                  </div>
                  <div>
                    Results file into{" "}
                    <span className="font-medium text-slate-900">{territoryLocation?.name || "the territory's location"}</span>
                  </div>
                </div>
              ) : null}
            </div>
          ) : mode === "city" ? (
            <>
              <div>
                <Label htmlFor="scan-city-input" className="text-xs uppercase tracking-wide text-slate-600">City</Label>
                <Input
                  id="scan-city-input"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  list="scan-city-suggestions"
                  placeholder="e.g. Edmonton, AB · Halifax, NS · Saskatoon"
                  autoComplete="off"
                  autoCapitalize="words"
                  className="mt-1 min-h-[44px] text-base"
                  data-testid="scan-city-input"
                />
                <datalist id="scan-city-suggestions">
                  {cityOptions.map((n) => <option key={n} value={n} />)}
                </datalist>
                <div className="mt-1 text-[11px] text-slate-500 inline-flex items-center gap-1">
                  <Info className="h-3 w-3" />
                  Canadian cities only — searches outside Canada will be rejected.
                </div>
              </div>
              <AreaPresetPicker value={expansion} onChange={setExpansion} />
            </>
          ) : (
            <SearchUrlPreview
              url={searchUrl}
              onUrlChange={setSearchUrl}
              maxResults={maxResults}
              onPreview={setUrlPreview}
            />
          )}

          <ScanBudgetControls
            maxResults={maxResults}
            onMaxResults={setMaxResults}
            usdCap={usdCap}
            onUsdCap={setUsdCap}
          />
          <CreditEstimate action="openhouse_scan" params={{ max_results: Number(maxResults) }} onAllowed={setCreditsOk} />

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange?.(false)} disabled={submitting} className="min-h-[44px]">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={disabled}
              className="min-h-[44px] text-white"
              style={{ background: "hsl(var(--brand))" }}
              data-testid="scan-submit-button"
            >
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              {submitting ? "Starting…" : "Start scan"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
